import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Main as Container } from 'Components/Main';
import { getAssetById, archiveAsset, unarchiveAsset } from 'services/assets-service';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { Dialog, PageHeader } from 'Components/ui';
import { formatDate as formatDateIntl, LOCALE_MAP } from 'utils/locale-formatting';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';

const EditBtn = styled.button`
  height: 40px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;
const ArchiveBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  width: fit-content;
`;
const TabBtn = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  background: ${p => p.active ? BLUE : 'transparent'};
  color: ${p => p.active ? 'white' : MUTED};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${p => p.active ? 600 : 500};
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
`;
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  box-sizing: border-box;
`;
const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;
const IconTile = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-celeste), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
`;
const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 6px;
`;
const FieldValue = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${TEXT};
`;
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  background: ${p => p.bg};
  color: ${p => p.color};
`;
const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 1 / -1;
`;
const TagChip = styled.span`
  background: var(--vo-capri-50);
  color: ${BLUE};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 12px;
  border-radius: 20px;
`;
const DescriptionField = styled.div`
  grid-column: 1 / -1;
`;
const DescriptionText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${TEXT};
  line-height: 1.65;
  margin: 0;
`;
const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const AvatarText = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: white;
`;
const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const SideCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const SideCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;
const HistoryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const RelatedRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const RelatedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${BG};
  border-radius: 10px;
`;
const RelatedLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;
const RelatedCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${BLUE};
`;

const BondsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;
const VinculoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const VinculoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;
const VinculoTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const AddVinculoBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: var(--vo-capri-50);
  color: ${BLUE};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const VinculoChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const VinculoChip = styled.span`
  display: inline-flex;
  align-items: center;
  background: var(--vo-cobalto-8);
  color: ${BLUE_DEEP};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 20px;
`;

const AssetSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
  </svg>
);
const EditSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const ArchiveSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);
const PlusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const CRIT_COLORS = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};
const TYPE_COLORS = {
  'Pessoa': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  'Tecnologia': { color: 'var(--vo-cobalto)', bg: 'color-mix(in srgb, var(--vo-cobalto) 10%, transparent)' },
  'Ambiente': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
};
export const AssetsShow = () => {
  const { t, i18n } = useTranslation(['asm', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const locale = LOCALE_MAP[i18n.language] ?? 'pt-BR';
  const formatDate = (dateStr) => formatDateIntl(dateStr, locale) || '—';

  const { isLoading, error, data: asset, refetch } = useQuery(['getAssetById', id], async () => {
    const dataAsset = await getAssetById(id);
    return dataAsset.status === 200 ? dataAsset.data : null;
  });

  const handleArchive = async () => {
    const isArchived = asset?.archived;
    const confirmMsg = isArchived
      ? `${t('asm:assets.delete.unarchive')} ${asset?.name}?`
      : `${t('asm:assets.delete.archive')} ${asset?.name}?`;
    const confirmed = await Dialog.confirm({
      title: confirmMsg,
      confirmLabel: isArchived ? t('asm:assets.show.unarchive') : t('asm:assets.show.archive'),
    });
    if (!confirmed) return;
    try {
      const res = isArchived ? await unarchiveAsset(id) : await archiveAsset(id);
      if (res.status === 200) {
        Dialog.alert({ title: 'Sucesso!', icon: 'success' });
        refetch();
      }
    } catch {
      Dialog.alert({ title: t('asm:assets.show.error'), icon: 'error' });
    }
  };

  if (isLoading) return <Container><div style={{ paddingTop: 32 }}><PageLoaderShow /></div></Container>;
  if (error || !asset) return <Container><div style={{ paddingTop: 32 }}>{t('asm:assets.show.error')}</div></Container>;

  const crit = asset.criticity ? CRIT_COLORS[asset.criticity] : null;
  const typeStyle = asset.type ? TYPE_COLORS[asset.type] : null;
  const activeAreas = asset.areas?.filter(a => !a.archived) ?? [];
  const activeProcesses = asset.processes?.filter(p => !p.archived) ?? [];
  const activeSystems = asset.systems?.filter(s => !s.archived) ?? [];

  return (
    <Container>
      <PageHeader
        title={asset.name}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('asm:assets.title'), route: '/assets' },
          { label: asset.name },
        ]}
        actions={
          <>
            <EditBtn onClick={() => navigate('/assets/edit/' + id)}>
              <EditSvg />
              {t('asm:assets.show.edit')}
            </EditBtn>
            <ArchiveBtn onClick={handleArchive}>
              <ArchiveSvg />
              {asset.archived ? t('asm:assets.show.unarchive') : t('asm:assets.show.archive')}
            </ArchiveBtn>
          </>
        }
      />

      <TabBar>
        <TabBtn active={activeTab === 0} onClick={() => setActiveTab(0)}>
          {t('asm:assets.show.tabDetails')}
        </TabBtn>
        <TabBtn active={activeTab === 1} onClick={() => setActiveTab(1)}>
          {t('asm:assets.show.tabBonds')}
        </TabBtn>
      </TabBar>

      {activeTab === 0 && (
        <MainGrid>
          <InfoCard>
            <SectionHead>
              <IconTile><AssetSvg /></IconTile>
              <SectionTitle>{t('asm:assets.show.infoSectionTitle')}</SectionTitle>
            </SectionHead>
            <FieldsGrid>
              <div>
                <FieldLabel>{t('asm:assets.show.criticality')}</FieldLabel>
                {crit
                  ? <Badge bg={crit.bg} color={crit.color}>{t(`asm:criticality.short.${asset.criticity}`)} ({asset.criticity})</Badge>
                  : <FieldValue>-</FieldValue>
                }
              </div>
              <div>
                <FieldLabel>{t('asm:assets.show.type')}</FieldLabel>
                {typeStyle
                  ? <Badge bg={typeStyle.bg} color={typeStyle.color}>{asset.type}</Badge>
                  : <FieldValue>{asset.type || '-'}</FieldValue>
                }
              </div>
              <div>
                <FieldLabel>{t('asm:assets.show.responsible')}</FieldLabel>
                {asset.user
                  ? <UserRow><Avatar><AvatarText>{getInitials(asset.user)}</AvatarText></Avatar><FieldValue>{asset.user}</FieldValue></UserRow>
                  : <FieldValue>-</FieldValue>
                }
              </div>
              <div>
                <FieldLabel>{t('asm:assets.show.pii')}</FieldLabel>
                {asset.pii
                  ? <Badge bg="var(--vo-danger-10)" color="var(--vo-danger)">{t('asm:assets.show.piiYes')}</Badge>
                  : <Badge bg="var(--vo-slate-10)" color={MUTED}>{t('asm:assets.show.piiNo')}</Badge>
                }
              </div>
              <div>
                <FieldLabel>{t('asm:assets.show.location')}</FieldLabel>
                <FieldValue>{asset.location || '-'}</FieldValue>
              </div>

              <TagsRow>
                <FieldLabel style={{ width: '100%' }}>{t('asm:assets.show.tags')}</FieldLabel>
                {asset.tags?.length > 0
                  ? asset.tags.map(tag => <TagChip key={tag}>{tag}</TagChip>)
                  : <FieldValue>-</FieldValue>
                }
              </TagsRow>

              <DescriptionField>
                <FieldLabel>{t('asm:assets.show.description')}</FieldLabel>
                <DescriptionText>{asset.description || '-'}</DescriptionText>
              </DescriptionField>
            </FieldsGrid>
          </InfoCard>

          <Sidebar>
            <SideCard>
              <SideCardTitle>{t('asm:assets.show.historySectionTitle')}</SideCardTitle>
              <HistoryRows>
                <div>
                  <FieldLabel>{t('asm:assets.show.createdAt')}</FieldLabel>
                  <DescriptionText>{formatDate(asset.createdAt)}</DescriptionText>
                </div>
                <div>
                  <FieldLabel>{t('asm:assets.show.updatedAt')}</FieldLabel>
                  <DescriptionText>{formatDate(asset.updatedAt)}</DescriptionText>
                </div>
              </HistoryRows>
            </SideCard>
            <SideCard>
              <SideCardTitle>{t('asm:assets.show.relatedDataTitle')}</SideCardTitle>
              <RelatedRows>
                <RelatedRow>
                  <RelatedLabel>{t('asm:assets.show.relatedAreas')}</RelatedLabel>
                  <RelatedCount>{activeAreas.length}</RelatedCount>
                </RelatedRow>
                <RelatedRow>
                  <RelatedLabel>{t('asm:assets.show.relatedProcesses')}</RelatedLabel>
                  <RelatedCount>{activeProcesses.length}</RelatedCount>
                </RelatedRow>
                <RelatedRow>
                  <RelatedLabel>{t('asm:assets.show.relatedSystems')}</RelatedLabel>
                  <RelatedCount>{activeSystems.length}</RelatedCount>
                </RelatedRow>
              </RelatedRows>
            </SideCard>
          </Sidebar>
        </MainGrid>
      )}

      {activeTab === 1 && (
        <BondsGrid>
          <VinculoCard>
            <VinculoHeader>
              <VinculoTitle>{t('asm:assets.show.bonds.areas')}</VinculoTitle>
              <AddVinculoBtn onClick={() => navigate('/assets/edit/' + id)}><PlusSvg /></AddVinculoBtn>
            </VinculoHeader>
            <VinculoChips>
              {activeAreas.map(area => (
                <VinculoChip key={area._id || area.name}>{area.name}</VinculoChip>
              ))}
            </VinculoChips>
          </VinculoCard>
          <VinculoCard>
            <VinculoHeader>
              <VinculoTitle>{t('asm:assets.show.bonds.processes')}</VinculoTitle>
              <AddVinculoBtn onClick={() => navigate('/assets/edit/' + id)}><PlusSvg /></AddVinculoBtn>
            </VinculoHeader>
            <VinculoChips>
              {activeProcesses.map(process => (
                <VinculoChip key={process._id || process.name}>{process.name}</VinculoChip>
              ))}
            </VinculoChips>
          </VinculoCard>
          <VinculoCard>
            <VinculoHeader>
              <VinculoTitle>{t('asm:assets.show.bonds.systems')}</VinculoTitle>
              <AddVinculoBtn onClick={() => navigate('/assets/edit/' + id)}><PlusSvg /></AddVinculoBtn>
            </VinculoHeader>
            <VinculoChips>
              {activeSystems.map(system => (
                <VinculoChip key={system._id || system.name}>{system.name}</VinculoChip>
              ))}
            </VinculoChips>
          </VinculoCard>
        </BondsGrid>
      )}
    </Container>
  );
};
